import {
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  InfoCircleOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Button, Card, Listy, Popover, Progress, Typography } from 'antd';
import { memo, useEffect, useMemo, useState } from 'react';
import api from 'api';
import cn from 'classnames';
import { availableIconFormat, documnentIcons } from 'helpers/documentIcons';
import { TFunction } from 'i18next';
import { DocumentUpload } from 'interfaces/IDocument';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { fetchRoom } from 'store/reducers/roomsCreator';
import { fetchUserPermissions } from 'store/reducers/userDataCreator';
import {
  clearUploadingDocuments,
  clearUploadingFolders,
  setCanceledUploads,
  setCurrentFolder,
  setUploadAbortController,
} from 'store/slices/dataDocumentsSlice';
import { setUserPermissions } from 'store/slices/dataPermissionsSlice';
import { setUserAndRoomStatus } from 'store/slices/userDataSlice';
import { getErrorPopoverContent } from './errorPopoverContent/errorPopoverContent';
import classes from './UploadProgressPanel.module.scss';

const statusToPercent: Record<NonNullable<DocumentUpload['status']>, number> = {
  INIT_UPLOAD: 20,
  TOKENIZE: 50,
  ENCRYPT: 60,
  STORAGE_UPLOAD: 75,
  UPLOADED: 100,
  FAILED: 100,
};

const calculateTotalProgress = (doc: DocumentUpload) => {
  if (doc.status === undefined && doc.uploadProgress === 100) {
    return 20;
  }

  if (doc.uploadProgress !== undefined && doc.uploadProgress !== 100) {
    return Math.min(20, Math.floor(doc.uploadProgress * 0.2));
  }
  return doc.status ? statusToPercent[doc.status] : 0;
};

const getDocumentIcon = (doc: DocumentUpload) => {
  const fileName = doc.document_name;
  const extension =
    doc.extension || fileName?.split('.').pop()?.toLowerCase() || '';

  const icon = availableIconFormat.includes(extension) ? extension : 'default';

  return documnentIcons[icon as keyof typeof documnentIcons];
};

const calculateFolderProgress = (
  uploadDocuments: DocumentUpload[],
  folderId?: string
) => {
  if (!uploadDocuments.length) return 0;
  const folderDocuments = folderId
    ? uploadDocuments.filter((doc) => doc.root_folder?.id === folderId)
    : uploadDocuments;

  const totalProgress = folderDocuments.reduce((sum, doc) => {
    return sum + calculateTotalProgress(doc);
  }, 0);

  return Math.floor(totalProgress / folderDocuments.length);
};

const formatProgress = (
  percent: number | undefined,
  isError: boolean = false
) => {
  if (isError) {
    return <CloseOutlined className={classes.errorSuccess} />;
  }
  if (percent === 100) {
    return <CheckOutlined className={classes.progressSuccess} />;
  }
  return (
    <span className={classes.progressText}>{`${percent}%`}</span>
  );
};

const getLocalizedText = (
  t: TFunction,
  key: 'uploading' | 'uploaded',
  count: number,
  language: string
) => {
  if (language === 'ru') {
    if (count === 1) {
      return t(`uploadProgressPanel.${key}_one`);
    } else if (count >= 2 && count <= 4) {
      return t(`uploadProgressPanel.${key}_few`, { count });
    } else {
      return t(`uploadProgressPanel.${key}_many`, { count });
    }
  }

  return t(`uploadProgressPanel.${key}`, { count });
};

export const UploadProgressPanel = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isCanceled, setIsCanceled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const dispatch = useAppDispatch();
  const { i18n, t } = useTranslation();
  const dataRoom = useAppSelector((state) => state.dataRoom.dataRoom);
  const { available_rooms } = useAppSelector((store) => store.userData);
  const { uploadingFolders }   = useAppSelector(
    (store) => store.documents
  );
  const { uploadingDocuments, uploadAbortController, canceledUploads, choosenDocuments } = useAppSelector(
    (store) => store.documents
  );
  const { isRSidebarCollapsed, rSidebarWidth, selectedKeys } = useAppSelector(
    (store) => store.windowState
  );
const selectedChat = useAppSelector((store) => store.chat.selectedChat);

  useEffect(() => {
    if (uploadingDocuments.length > 0 || uploadingFolders.length > 0) {
      setIsOpen(true);
      setIsCanceled(false);
    }
  }, [uploadingDocuments, uploadingFolders]);

  const onClose = () => {
    setIsOpen(false);
    setIsCanceled(false);
    dispatch(clearUploadingDocuments());
    dispatch(clearUploadingFolders());
  };

  const onLoadingCancel = async () => {
    setIsCanceled(true);
    uploadAbortController?.abort();

    const docIdsToCancel = uploadingDocuments
      .filter((d) => d.status !== 'UPLOADED')
      .map((d) => d.document_id);

    const foldersToCancel = uploadingFolders.filter((folder) => {
      const folderDocuments = uploadingDocuments.filter(
        (doc) => doc.root_folder?.id === folder.id
      );

      return !folderDocuments.every((doc) => doc.status === 'UPLOADED');
    });

    const folderIdsToCancel = foldersToCancel
      .map((folder) => folder.id)
      .filter((id): id is string => Boolean(id));

    const allCanceledIds = [...docIdsToCancel, ...folderIdsToCancel];
    dispatch(setCanceledUploads(allCanceledIds));

    const currentFolderId = params.folderId;
    if (currentFolderId && folderIdsToCancel.includes(currentFolderId)) {
      const pathParts = location.pathname.split('/');
      pathParts.pop();
      const newPath = pathParts.join('/');
      navigate(newPath);
    }

    const isTemporaryId = (id: string) => id.includes('temp_');

    if (docIdsToCancel.length > 0) {
      const permanentDocIds = docIdsToCancel.filter(
        (id) => id && !isTemporaryId(id)
      );

      try {
        await api.deleteDocuments(permanentDocIds, {
          room_id: dataRoom?.id,
          include_versions: false,
          action: 'delete',
          details: {
            documents_names: permanentDocIds.join(', '),
            documents_ids: permanentDocIds.join(', '),
          },
        });
      } catch (error) {
        console.error(
          `Ошибка при удалении документов ${permanentDocIds}:`,
          error
        );
      }
    }

    for (const folderId of folderIdsToCancel) {
      if (folderId) {
        try {
          await api.deleteFolder(folderId);
        } catch (error) {
          console.error(`Ошибка при удалении папки ${folderId}:`, error);
        }
      }
    }
    dispatch(setUploadAbortController(null));
  };

  const onNavigate = async (item: DocumentUpload) => {
    if (item.status === 'UPLOADED' && item.document_id && item.room_id) {
      const targetRoom = available_rooms?.find(
        (room) => room.id === item.room_id
      );

      if (!targetRoom) {
        return;
      }

      if (item.folder_id && item.folder_id !== 'None') {
        try {
          const folder = await api.getFolderById(item.folder_id, item.room_id);
          const folderPath = folder.data.folder_path
            ? folder.data.folder_path?.reverse()
            : [];
          dispatch(setCurrentFolder(folderPath));
          localStorage.setItem(
            'previousFolderPath',
            JSON.stringify(folderPath)
          );
          navigate(
            `/room/${targetRoom.custom_url || targetRoom.id}/documents/${
              item.folder_id
            }`
          );
        } catch (error) {
          console.error(error);
        }
      } else {
        dispatch(setCurrentFolder([]));
        navigate(`/room/${targetRoom.custom_url || targetRoom.id}/documents`);
      }
      if (dataRoom?.id !== item.room_id) {
        dispatch(setUserPermissions(null));
        await dispatch(fetchUserPermissions({ idRoom: item.room_id }));
        await dispatch(fetchRoom({ id: item.room_id }));
        dispatch(setUserAndRoomStatus('fulfilledDataRoom'));
      }
    }
  };

  const hasError = uploadingDocuments.some((item) => item.status === 'FAILED');

  const isAllFinished = uploadingDocuments.every(
    (document) =>
      document.status === 'UPLOADED' ||
      document.status === 'FAILED' ||
      canceledUploads.includes(document.document_id)
  );

  const activeUploadingCount = uploadingDocuments.filter(
    (document) =>
      document.status !== 'UPLOADED' &&
      document.status !== 'FAILED' &&
      !canceledUploads.includes(document.document_id)
  ).length;

  const panelPosition = useMemo(() => {
    const baseRightOffset = 16;
    const footerHeight = 48;
    const baseBottomOffset = 16;

    const rightOffset = isRSidebarCollapsed
      ? baseRightOffset
      : rSidebarWidth + baseRightOffset;

    const bottomOffset =
      choosenDocuments.length > 0 || (selectedChat || selectedKeys.includes('chat'))
        ? baseBottomOffset + footerHeight
        : baseBottomOffset;

    return {
      right: rightOffset,
      bottom: bottomOffset,
    };
  }, [isRSidebarCollapsed, rSidebarWidth, choosenDocuments.length, selectedChat, selectedKeys]);

  return isOpen ? (
    <div
      className={classes.UploadProgressPanel}
      style={{
        right: `${panelPosition.right}px`,
        bottom: `${panelPosition.bottom}px`,
      }}
    >
      <Card
        className={classes.Card}
        size='small'
        styles={{
          header: { padding: 0 },
          body: isExpanded
            ? { padding: '4px', maxHeight: 220, overflowY: 'auto' }
            : { display: 'none' },
        }}
        title={
          <div className={classes.title}>
            <div className={classes.headerWrap}>
              <Typography.Text strong>
                {t('uploadProgressPanel.title')}
              </Typography.Text>
              <div className={classes.btnsWrap}>
                {hasError && (
                  <Popover content={getErrorPopoverContent(t)}>
                    <InfoCircleOutlined />
                  </Popover>
                )}
                <Button
                  type='text'
                  icon={isExpanded ? <DownOutlined /> : <UpOutlined />}
                  onClick={() => setIsExpanded((prev) => !prev)}
                />
                <Button
                  type='text'
                  icon={<CloseOutlined />}
                  onClick={onClose}
                />
              </div>
            </div>
            {isExpanded &&
              activeUploadingCount > 0 &&
              !isAllFinished &&
              !isCanceled && (
                <div className={classes.cancelWrap}>
                  <Typography.Text>
                    {isAllFinished
                      ? getLocalizedText(
                          t,
                          'uploaded',
                          uploadingDocuments.length,
                          i18n.language
                        )
                      : getLocalizedText(
                          t,
                          'uploading',
                          activeUploadingCount,
                          i18n.language
                        )}
                  </Typography.Text>
                  <Button
                    onClick={onLoadingCancel}
                    color='primary'
                    variant='link'
                  >
                    {t('uploadProgressPanel.cancel')}
                  </Button>
                </div>
              )}
          </div>
        }
      >
        {uploadingFolders.length > 0 && (
          <Listy
            className={classes.uploadList}
            items={uploadingFolders}
            rowKey='id'
            itemRender={(item) => {
              const { id } = item;
              const isCanceled = id ? canceledUploads.includes(id) : false;

              return (
                <div
                  key={id}
                  className={cn(classes.listItem, classes.folder)}
                >
                  <div className={classes.list_meta}>
                    {documnentIcons['folder']}

                    <div className={classes.documentName}>
                      {item.name}
                    </div>
                  </div>

                  <div className={classes.actions}>
                    {isCanceled ? (
                      <span>{t('uploadProgressPanel.uploadCanceled')}</span>
                    ) : (
                      <Progress
                        type='circle'
                        percent={calculateFolderProgress(uploadingDocuments, id!)}
                        size={28}
                        format={(percent) => formatProgress(percent)}
                      />
                    )}
                  </div>
                </div>
              );
            }}
          />
        )}
        {uploadingDocuments.length > 0 && (
          <Listy
            className={classes.uploadList}
            items={uploadingDocuments.filter((d) => !d.root_folder)}
            rowKey='document_id'
            itemRender={(item) => {
              const { status, document_id } = item;
              const isError = status === 'FAILED';
              const isSuccess = status === 'UPLOADED';
              const isCanceled = canceledUploads.includes(document_id);

              return (
                <div
                  key={document_id}
                  onClick={() => onNavigate(item)}
                  className={cn(
                    classes.listItem,
                    classes.document,
                    isSuccess && classes.success,
                    isError && classes.error
                  )}
                >
                  <div className={classes.list_meta}>
                    {getDocumentIcon(item)}

                    <div className={classes.text_block}>
                      <div className={classes.documentName}>
                        {item.document_name}
                      </div>

                      {isError && (
                        <Typography.Text className={classes.errorText}>
                          {t('uploadProgressPanel.error.uploadFailure')}
                        </Typography.Text>
                      )}
                    </div>
                  </div>

                  <div className={classes.actions}>
                    {isCanceled ? (
                      <span>{t('uploadProgressPanel.uploadCanceled')}</span>
                    ) : (
                      <Progress
                        type='circle'
                        percent={calculateTotalProgress(item)}
                        size={28}
                        status={isError ? 'exception' : undefined}
                        format={(percent) => formatProgress(percent, isError)}
                      />
                    )}
                  </div>
                </div>
              );
            }}
          />
        )}
      </Card>
    </div>
  ) : null;
});
